import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { createCheckout, createCryptoCheckout, money, fetchBundleTiers, bundleDiscount, fetchLoyalty, fetchPairings } from "../lib/api";
import { Minus, Plus, Trash2, Loader2, Award, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { BundleSaveMeter } from "./BundleSaveMeter";
import { toast } from "sonner";
import { track } from "../lib/pixel";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, addItem, subtotal, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [ack, setAck] = useState(false);
  const [pairings, setPairings] = useState([]);
  const [cardEmail, setCardEmail] = useState(() => localStorage.getItem("rsa_email") || "");

  useEffect(() => {
    fetchBundleTiers().then((d) => setTiers(d.tiers)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) fetchLoyalty().then(setLoyalty).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !items.length) {
      setPairings([]);
      return;
    }
    fetchPairings(items.map((i) => i.handle)).then(setPairings).catch(() => {});
  }, [open, items]);

  const stackPercent = bundleDiscount(count, tiers);
  const memberPercent = loyalty?.tier_percent || 0;
  const discount = Math.min(35, stackPercent + memberPercent);
  const savings = subtotal * (discount / 100);
  const total = subtotal - savings;

  const CODE_BY_STACK = { 15: "STACK15", 20: "STACK20", 30: "STACK30" };
  const CODE_BY_LOYALTY = { 3: "RSCONTENDER", 6: "RSELITE", 10: "RSCHAMPION" };
  const cryptoPct = Math.max(stackPercent, memberPercent);
  const cryptoCode = stackPercent >= memberPercent ? CODE_BY_STACK[stackPercent] : CODE_BY_LOYALTY[memberPercent];

  const checkout = async () => {
    if (!items.length) return;
    if (!ack) {
      toast.error("Please confirm the research-use declaration to continue.");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(
        items.map((i) => ({ handle: i.handle, quantity: i.quantity, variant: i.variant || null })),
        ack
      );
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const cryptoCheckout = async () => {
    if (!items.length) return;
    if (!ack) {
      toast.error("Please confirm the research-use declaration to continue.");
      return;
    }
    const email = cardEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter your email so we can send your receipt and order updates.");
      return;
    }
    localStorage.setItem("rsa_email", email);
    setCryptoLoading(true);
    track("InitiateCheckout", { value: subtotal, currency: "AUD", num_items: items.length });
    try {
      const { redirect_url } = await createCryptoCheckout(
        items.map((i) => ({ handle: i.handle, quantity: i.quantity, variant: i.variant || null })),
        ack,
        email
      );
      window.location.href = redirect_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Card checkout is unavailable right now — please try again in a moment.";
      toast.error(msg);
      setCryptoLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-ink-900 border-l border-[#27272A] text-white p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-[#27272A]">
          <SheetTitle className="font-display font-extrabold text-2xl tracking-tight text-white flex items-baseline gap-3">
            YOUR CART
            <span className="font-mono-tech text-xs tracking-widest text-zinc-500">
              {count} ITEM{count === 1 ? "" : "S"}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            Review your cart items, bundle savings and checkout options
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-20">
              <p className="font-display text-xl text-zinc-500">Your cart is empty</p>
              <p className="font-mono-tech text-xs tracking-widest text-zinc-600 uppercase">
                Add a protocol to begin
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((i) => (
                <div key={i.key || i.handle} className="flex gap-3" data-testid={`cart-item-${i.key || i.handle}`}>
                  <div className="h-14 w-14 shrink-0 bg-ink-700 border border-[#27272A] overflow-hidden">
                    <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <p className="font-display font-bold text-sm leading-tight">
                        {i.name}{" "}
                        <span className="font-mono-tech text-[10px] font-normal tracking-wider text-zinc-500 uppercase">
                          {i.dose}
                        </span>
                      </p>
                      <button
                        onClick={() => removeItem(i.key || i.handle)}
                        className="text-zinc-600 hover:text-accent transition-colors shrink-0"
                        data-testid={`cart-remove-${i.key || i.handle}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <div className="flex items-center border border-[#27272A] bg-ink-900">
                        <button
                          onClick={() => updateQty(i.key || i.handle, i.quantity - 1)}
                          className="h-9 w-9 flex items-center justify-center text-zinc-400 hover:bg-accent hover:text-white transition-colors"
                          data-testid={`cart-dec-${i.key || i.handle}`}
                        >
                          <Minus size={15} />
                        </button>
                        <span className="w-10 text-center font-display font-bold text-base" data-testid={`cart-qty-${i.key || i.handle}`}>
                          {i.quantity}
                        </span>
                        <button
                          onClick={() => updateQty(i.key || i.handle, i.quantity + 1)}
                          className="h-9 w-9 flex items-center justify-center text-zinc-400 hover:bg-accent hover:text-white transition-colors"
                          data-testid={`cart-inc-${i.key || i.handle}`}
                        >
                          <Plus size={15} />
                        </button>
                      </div>
                      <span className="font-display font-bold text-base">{money(i.price * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {pairings.length > 0 && (
                <div className="pt-4 mt-1 border-t border-[#27272A]" data-testid="pairings-section">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2.5">
                    Pairs well with your cart
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {pairings.map((p) => (
                      <div
                        key={p.handle}
                        className="border border-[#27272A] bg-ink-900 p-2 flex flex-col"
                        data-testid={`pairing-${p.handle}`}
                      >
                        <div className="h-14 w-full bg-ink-700 overflow-hidden mb-1.5">
                          <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                        </div>
                        <p className="font-display font-bold text-[11px] leading-tight truncate">{p.name}</p>
                        <p className="font-mono-tech text-[9px] text-zinc-500 uppercase tracking-wider truncate">
                          {p.dose}
                        </p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="font-mono-tech text-[11px] text-zinc-300">{money(p.price)}</span>
                          <button
                            onClick={() => {
                              addItem({ ...p, images: [p.image] }, 1);
                              toast.success(`${p.name} added to cart`);
                            }}
                            className="border border-accent text-accent hover:bg-accent hover:text-white transition-colors p-1.5 clip-corner"
                            data-testid={`pairing-add-${p.handle}`}
                            aria-label={`Add ${p.name}`}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[#27272A] px-6 py-4 space-y-2.5">
          {items.length > 0 && (
            <BundleSaveMeter
              count={count}
              subtotal={subtotal}
              tiers={tiers}
              stackPercent={stackPercent}
              onNavigate={() => setOpen(false)}
            />
          )}

          {loyalty && memberPercent > 0 && (
            <div className="flex items-center gap-2 text-amber-400 border border-amber-400/30 bg-amber-400/5 px-3.5 py-2.5 clip-corner">
              <Award size={14} />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">
                {loyalty.tier} member · {memberPercent}% off applied
              </span>
            </div>
          )}

          {discount > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between font-mono-tech text-xs text-zinc-400">
                <span>Subtotal</span>
                <span>{money(subtotal)}</span>
              </div>
              {stackPercent > 0 && (
                <div className="flex items-center justify-between font-mono-tech text-xs text-accent">
                  <span>Stack saving ({stackPercent}%)</span>
                  <span>− {money(subtotal * (stackPercent / 100))}</span>
                </div>
              )}
              {memberPercent > 0 && (
                <div className="flex items-center justify-between font-mono-tech text-xs text-amber-400">
                  <span>{loyalty.tier} member ({memberPercent}%)</span>
                  <span data-testid="cart-savings">− {money(subtotal * (memberPercent / 100))}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">
              {discount > 0 ? "Total" : "Subtotal"}
            </span>
            <span className="font-display font-extrabold text-2xl" data-testid="cart-subtotal">
              {money(total)}
            </span>
          </div>
          <p className="font-mono-tech text-[10px] text-zinc-600 tracking-wide">
            Free tracked shipping on orders over $250 · 7–14 business days.
          </p>

          <label className="flex gap-2.5 items-start cursor-pointer select-none" data-testid="compliance-checkbox">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#FF331F] shrink-0 cursor-pointer"
              data-testid="compliance-checkbox-input"
            />
            <span className="text-[11px] text-zinc-400 leading-snug">
              I am <strong className="text-zinc-200">18+</strong> and confirm these products are for{" "}
              <strong className="text-zinc-200">laboratory research use only — not for human
              consumption</strong>. I agree to the{" "}
              <Link to="/policies" onClick={() => setOpen(false)} className="text-accent underline underline-offset-2">
                terms &amp; research policy
              </Link>
              .
            </span>
          </label>

          <div>
            <input
              type="email"
              value={cardEmail}
              onChange={(e) => setCardEmail(e.target.value)}
              placeholder="Email for receipt & order updates"
              className="w-full bg-ink-800 border border-[#27272A] focus:border-accent outline-none text-white font-mono-tech text-sm px-4 py-3 placeholder:text-zinc-600 transition-colors"
              data-testid="card-email-input"
            />
          </div>
          <button
            onClick={cryptoCheckout}
            disabled={!items.length || !ack || cryptoLoading}
            className="w-full bg-white hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed text-ink-900 font-display font-bold uppercase tracking-wide text-base py-3 flex items-center justify-center gap-2 transition-colors clip-corner"
            data-testid="card-checkout-button"
          >
            {cryptoLoading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
            {cryptoLoading ? "Opening secure checkout…" : "Pay by Card"}
          </button>
          {cryptoCode && (
            <p className="font-mono-tech text-[10px] text-zinc-500 tracking-wide text-center -mt-1" data-testid="card-discount-hint">
              Enter code <span className="text-accent font-bold">{cryptoCode}</span> at checkout for {cryptoPct}% off
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
